/**
 * Built for security — consolidated section
 *
 * Merges the previous three sections:
 *   1. "Built To Keep Your Crypto In Your Hands" → keeps "Offline by design"
 *   2. "Built Different" → keeps product video + unique specs (Qi charging, compact design)
 *   3. "Zero Points of Failure" → keeps "Works Even With Zero Battery" + IP67
 *
 * Single dark section with two-column layout: video left, specs right.
 */

import { Shield, Wifi, Zap, Maximize2 } from 'lucide-react';

/* ── Inline SVG for IP67 shield ── */
function ShieldCheckIcon() {
  return (
    <svg viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-10 h-10">
      <rect width="40" height="40" rx="10" fill="rgba(106,99,245,0.12)" />
      <path d="M20 8 L30 13 L30 21 Q30 28 20 33 Q10 28 10 21 L10 13 Z" stroke="#6a63f5" strokeWidth="1.5" fill="none" />
      <path d="M15 20 L18 23 L25 16" stroke="#6a63f5" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

const specs = [
  {
    icon: 'shield',
    title: 'Offline by design',
    subtitle: 'Air-gapped',
    desc: 'No Wi-Fi, no Bluetooth, no USB data connection. Your keys never touch the internet. Every transaction requires a physical NFC tap — eliminating remote attack vectors entirely.',
  },
  {
    icon: 'zap',
    title: 'Works even with zero battery',
    subtitle: 'Always accessible',
    desc: 'Even with a completely dead battery, your Ryder One still works by placing it on a wireless charger. Your crypto is never locked.',
  },
  {
    icon: 'battery',
    title: 'Qi wireless charging',
    subtitle: 'Rechargeable battery',
    desc: 'Built-in rechargeable battery with Qi wireless charging. Just place it on any Qi charger — the included wireless charger keeps your device always ready.',
  },
  {
    icon: 'ruler',
    title: 'Compact design',
    subtitle: '41 × 55 × 14.5mm',
    desc: 'Pocket-sized and portable. No USB ports, no mechanical buttons — nothing to break, nothing to exploit. Just pure, uncompromising security.',
  },
];

interface BuiltForSecuritySectionProps {
  isEventPage?: boolean;
  eventColors?: { accentDark?: string } | null;
}

export default function BuiltForSecuritySection({ isEventPage, eventColors }: BuiltForSecuritySectionProps) {
  return (
    <section
      id="technology"
      className="py-16 sm:py-24 overflow-hidden"
      style={{ backgroundColor: isEventPage && eventColors?.accentDark ? eventColors.accentDark : '#0a0a0f' }}
    >
      <div className="container px-4 max-w-6xl mx-auto">
        <div className="grid lg:grid-cols-2 gap-10 lg:gap-14 items-center">
          {/* Left: Product video */}
          <div className="relative rounded-2xl overflow-hidden max-w-sm sm:max-w-md lg:max-w-lg mx-auto aspect-square">
            <video
              src="https://ryder.id/cdn/shop/videos/c/vp/f5af8fe6d01a47b8bea9e271bb655f8d/f5af8fe6d01a47b8bea9e271bb655f8d.HD-1080p-2.5Mbps-38443582.mp4?v=0"
              autoPlay
              loop
              muted
              playsInline
              className="w-full h-full object-cover"
            />
          </div>

          {/* Right: Specs content */}
          <div>
            <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/5 text-sm text-white/60 mb-4">
              <Shield className="w-4 h-4" /> Engineering
            </span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4 sm:mb-6 tracking-tight">
              Built for security
            </h2>
            <p className="text-white/50 text-base sm:text-lg mb-8 sm:mb-10 leading-relaxed">
              Every layer of Ryder One is engineered for one purpose: keeping your crypto safe without the complexity.
            </p>

            <div className="space-y-6">
              {specs.map((spec, idx) => (
                <div key={idx} className="flex gap-4 group">
                  <div className="w-10 h-10 rounded-lg bg-[#6a63f5]/10 flex items-center justify-center flex-shrink-0 group-hover:bg-[#6a63f5]/20 transition-colors">
                    {spec.icon === 'shield' && <Wifi className="w-5 h-5 text-[#6a63f5]" />}
                    {spec.icon === 'zap' && <Zap className="w-5 h-5 text-[#6a63f5]" />}
                    {spec.icon === 'battery' && <Zap className="w-5 h-5 text-[#6a63f5]" />}
                    {spec.icon === 'ruler' && <Maximize2 className="w-5 h-5 text-[#6a63f5]" />}
                  </div>
                  <div>
                    <h3 className="font-semibold text-white mb-1">{spec.title}</h3>
                    <span className="inline-flex text-xs px-2 py-0.5 rounded-full bg-white/5 text-white/40 mb-2">{spec.subtitle}</span>
                    <p className="text-sm text-white/50 leading-relaxed">{spec.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* IP67 Rating Bar */}
        <div className="mt-10 sm:mt-14 bg-white/[0.03] border border-white/[0.08] rounded-2xl px-6 py-5 sm:px-8 sm:py-6 flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4 max-w-3xl mx-auto">
          <ShieldCheckIcon />
          <div className="text-center sm:text-left">
            <p className="text-base font-bold text-white">IP67 rated</p>
            <p className="text-sm text-white/50">Dust-tight. Waterproof up to 1m for 30 minutes.</p>
          </div>
        </div>
      </div>
    </section>
  );
}
